import {
  BuildingOffice2Icon,
  FolderIcon,
  UsersIcon,
  TagIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  EyeSlashIcon,
  ShoppingBagIcon,
  EnvelopeOpenIcon,
  ChartBarIcon,
  BookOpenIcon,
  DocumentDuplicateIcon,
  RectangleStackIcon,
  MegaphoneIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  BellIcon,
  ShieldCheckIcon,
  CalculatorIcon,
} from "@heroicons/react/24/outline";
import { NavigationItem } from "./types";
import { GraduationCap, Percent, TicketIcon } from "lucide-react";

/**
 * Each NavigationItem can have:
 * - name (string)
 * - href (string)
 * - icon (React component)
 * - current (boolean)
 * - internalName (string)
 * - permissions (string[]) - nav-item based permissions
 * - isCertainPathOnly (boolean)
 * - children?: NavigationItem[]
 */
const navigation: NavigationItem[] = [
  {
    name: "Events",
    href: "/admin/events",
    icon: CalendarDaysIcon,
    current: false,
    internalName: "event-registration-admin",
    permissions: ["administration-events"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Management",
        href: "/admin/events",
        icon: ClipboardDocumentListIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event-management",
      },
      {
        name: "Categories",
        href: "/admin/event-categories",
        icon: FolderIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_categories",
      },
      {
        name: "Instructors",
        href: "/admin/event-instructors",
        icon: GraduationCap,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_instructors",
      },
      {
        name: "Venues",
        href: "/admin/venues",
        icon: MapPinIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_venues",
      },
      {
        name: "Vouchers",
        href: "/admin/vouchers",
        icon: TicketIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_vouchers",
      },
      {
        name: "Discounts",
        href: "/admin/discounts",
        icon: Percent,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_discounts",
      },
      {
        name: "Notifications",
        href: "/admin/event-notifications",
        icon: BellIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-event-notifications"],
        internalName: "event_notifications",
      },
    ],
  },

  {
    name: "Email Management",
    href: "/admin/email-templates",
    icon: EnvelopeIcon,
    current: false,
    internalName: "email_management",
    permissions: ["administration-email-management"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Email Templates",
        href: "/admin/email-templates",
        icon: DocumentDuplicateIcon,
        current: false,
        internalName: "email_templates",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Email Campaigns",
        href: "/admin/email-campaigns",
        icon: MegaphoneIcon,
        current: false,
        internalName: "email_campaigns",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Template Categories",
        href: "/admin/email-template-categories",
        icon: FolderIcon,
        current: false,
        internalName: "email_template_categories",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Email Event Logs",
        href: "/admin/email/activity",
        icon: ChartBarIcon,
        current: false,
        internalName: "email_event_logs",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
    ],
  },

  {
    name: "Resource Library",
    href: "#",
    icon: BookOpenIcon,
    current: false,
    internalName: "resource_management",
    permissions: ["administration-resource-library"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Resource Management",
        href: "/admin/resources",
        icon: RectangleStackIcon,
        current: false,
        internalName: "resource_management",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
      {
        name: "File Manager",
        href: "#file-manager",
        icon: FolderIcon,
        current: false,
        internalName: "file_manager",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
        isDialog: true,
        dialogAction: "fileManager",
      },
      {
        name: "Resource Categories",
        href: "/admin/resource-categories",
        icon: FolderIcon,
        current: false,
        internalName: "resourceCategories",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
      {
        name: "Resource Tags",
        href: "/admin/resource-tags",
        icon: TagIcon,
        current: false,
        internalName: "resourceTags",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Company & Users",
    href: "#",
    icon: BuildingOffice2Icon,
    current: false,
    internalName: "user_management",
    permissions: ["administration-companies", "administration-users"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Company Management",
        href: "/admin/companies",
        icon: BuildingOffice2Icon,
        current: false,
        internalName: "company_management",
        permissions: ["administration-companies"],
        isCertainPathOnly: true,
      },
      {
        name: "User Management",
        href: "/admin/users",
        icon: UsersIcon,
        current: false,
        internalName: "admin_user_list",
        permissions: ["administration-users"],
        isCertainPathOnly: true,
      },
      {
        name: "Business Roles",
        href: "/admin/business-roles",
        icon: ShieldCheckIcon,
        current: false,
        internalName: "business_roles",
        permissions: ["administration-users"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Lead Generation",
    href: "#",
    icon: ChartBarIcon,
    current: false,
    internalName: "admin",
    permissions: ["administration-stochastic"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Campaign Status",
        href: "/lead-generation/campaign-status",
        icon: ChartBarIcon,
        current: false,
        internalName: "stochastic_campaign_status",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Campaign Job Status",
        href: "/lead-generation/campaign-job-status",
        icon: MegaphoneIcon,
        current: false,
        internalName: "stochastic_mailing",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Campaign Billing",
        href: "/lead-generation/campaigns/billing",
        icon: CurrencyDollarIcon,
        current: false,
        internalName: "campaign_billing",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Postage Expense",
        href: "/lead-generation/campaigns/postage",
        icon: EnvelopeOpenIcon,
        current: false,
        internalName: "campaignPostage",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Do Not Mail",
        href: "/lead-generation/do-not-mail",
        icon: EyeSlashIcon,
        current: false,
        internalName: "doNotMail",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Manage Products",
        href: "/lead-generation/products/campaign",
        icon: ShoppingBagIcon,
        current: false,
        internalName: "campaignProducts",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Draft Invoice Calculator",
        href: "/lead-generation/draft-invoice",
        icon: CalculatorIcon,
        current: false,
        internalName: "draft_invoice_calculator",
        permissions: ["administration-stochastic"],
      },
    ],
  },
];

const adminConfig = {
  sectionName: "admin",
  defaultRoute: "/admin/companies",
  navigation,
};

export default adminConfig;
